import DownloadIcon from '@mui/icons-material/Download';
import { Box, Checkbox, IconButton, Skeleton, TableContainer, TableFooter, TablePagination, Tooltip, Typography } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { downloadFile, formatDateForFilename, tableToCSV } from '../../../utils/exportUtils';
import './custom-table-pagination.css';
import { CustomTableProps, RowId, TableComponents } from './types';

const DEFAULT_ROWS_FOR_SKELETON = 8;

interface Props extends CustomTableProps {
  components?: TableComponents;
  useStickyBottomRow?: boolean;
  maxHeight?: string | number;
  stickyHeader?: boolean;
  stickyPagination?: boolean;
  tableSize?: 'small' | 'medium';
  loading?: boolean;
  exportable?: boolean;
  exportFilename?: string;
  title?: string;
}

export const CustomTable = ({
  columns,
  rows,
  minWidth = 600,
  defaultText = 'No data available',
  components = {},
  useStickyBottomRow = false,
  maxHeight,
  stickyHeader = true,
  stickyPagination = false,
  tableSize = 'small',
  pagination,
  columnGroups = [],
  loading = false,
  exportable = false,
  exportFilename,
  title,
  selection,
}: Props) => {
  const {
    TableContainer: CustomTableContainer = TableContainer,
    Table: CustomTable = Table,
    TableCell: CustomTableCell = TableCell,
    TableRow: CustomTableRow = TableRow,
    TablePagination: CustomTablePagination = TablePagination,
  } = components;

  const isSelectable = !!selection?.selectable;
  const getRowId = selection?.getRowId ?? ((row: Record<string, any>) => row.id as RowId);
  const isRowSelectable = selection?.isRowSelectable ?? (() => true);
  const selectableRows = isSelectable ? rows.filter(isRowSelectable) : [];
  const selectableIds = selectableRows.map(getRowId);
  const selectedSet = new Set(selection?.selectedRowIds ?? []);
  const selectedSelectableCount = selectableIds.filter((id) => selectedSet.has(id)).length;
  const allSelected =
    isSelectable && selectableIds.length > 0 && selectedSelectableCount === selectableIds.length;
  const someSelected = isSelectable && selectedSelectableCount > 0 && !allSelected;

  const handleSelectAllToggle = () => {
    if (!selection) return;
    const otherSelected = (selection.selectedRowIds ?? []).filter(
      (id) => !selectableIds.includes(id),
    );
    if (allSelected) {
      selection.onSelectionChange(otherSelected);
    } else {
      selection.onSelectionChange([...otherSelected, ...selectableIds]);
    }
  };

  const handleRowSelectToggle = (rowId: RowId) => {
    if (!selection) return;
    const current = selection.selectedRowIds ?? [];
    if (current.includes(rowId)) {
      selection.onSelectionChange(current.filter((id) => id !== rowId));
    } else {
      selection.onSelectionChange([...current, rowId]);
    }
  };

  const handleExport = () => {
    const csv = tableToCSV(columns, rows);
    const filename = exportFilename || `table-export-${formatDateForFilename()}.csv`;
    downloadFile(csv, filename);
  };

  if (!columns.length && !rows.length) {
    return null;
  }

  return (
    <>
      {title && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant='h6'>{title}</Typography>
          {exportable && rows.length > 0 && (
            <Tooltip title='Export to CSV'>
              <IconButton onClick={handleExport} size='small' sx={{ color: '#1a667a' }}>
                <DownloadIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      <CustomTableContainer sx={{ ...(maxHeight && { maxHeight }) }}>
        <CustomTable sx={{ minWidth }} size={tableSize} stickyHeader={stickyHeader}>
          <TableHead>
            {columnGroups && columnGroups.length > 0 && (
              <>
                {columnGroups.map((group, index) => {
                  const emptyCellsBefore =
                    group.startColumn -
                    (index > 0
                      ? columnGroups[index - 1].startColumn + columnGroups[index - 1].colspan
                      : 0);

                  return (
                    <React.Fragment key={group.id}>
                      {emptyCellsBefore > 0 &&
                        Array.from({ length: emptyCellsBefore }).map((_, i) => (
                          <CustomTableCell key={`empty-${group.id}-${i}`} />
                        ))}
                      <CustomTableCell
                        colSpan={group.colspan}
                        align='center'
                        sx={{
                          fontWeight: 'bold',
                          border: '1px #e0e0e0 solid',
                        }}>
                        {group.title}
                      </CustomTableCell>
                    </React.Fragment>
                  );
                })}

                {columns.length -
                  (columnGroups[columnGroups.length - 1].startColumn +
                    columnGroups[columnGroups.length - 1].colspan) >
                  0 &&
                  Array.from({
                    length:
                      columns.length -
                      (columnGroups[columnGroups.length - 1].startColumn +
                        columnGroups[columnGroups.length - 1].colspan),
                  }).map((_, i) => <CustomTableCell key={`empty-end-${i}`} />)}
              </>
            )}

            <CustomTableRow>
              {isSelectable && (
                <CustomTableCell padding='checkbox'>
                  <Checkbox
                    size='small'
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAllToggle}
                    inputProps={{ 'aria-label': 'select all rows' }}
                    disabled={selectableIds.length === 0}
                  />
                </CustomTableCell>
              )}
              {columns.map((column) => (
                <CustomTableCell
                  key={column.key}
                  align={column.align || 'left'}
                  width={column.width}
                  sx={{
                    minWidth: column.minWidth,
                    maxWidth: column.width,
                  }}>
                  <strong>{column.label}</strong>
                </CustomTableCell>
              ))}
            </CustomTableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: DEFAULT_ROWS_FOR_SKELETON }).map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {isSelectable && (
                    <TableCell padding='checkbox'>
                      <Skeleton variant='rectangular' width={20} height={20} />
                    </TableCell>
                  )}
                  {Array.from({ length: columns.length }).map((_, colIndex) => (
                    <TableCell key={colIndex}>
                      <Skeleton variant='text' animation='wave' />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length > 0 ? (
              rows.map((row, rowIndex) => {
                const rowId = isSelectable ? getRowId(row) : undefined;
                const rowSelectable = isSelectable ? isRowSelectable(row) : false;
                const isSelected = rowId !== undefined && selectedSet.has(rowId);
                return (
                  <CustomTableRow
                    key={`custom-row-${
                      useStickyBottomRow && (rows.length - 1 === rowIndex || rows.length === 0)
                        ? rowIndex * Number.MAX_SAFE_INTEGER
                        : rowIndex
                    }`}
                    onClick={row.onClick}
                    hover
                    selected={isSelected}
                    sx={{ cursor: row.onClick ? 'pointer' : 'default' }}>
                    {isSelectable && (
                      <CustomTableCell
                        padding='checkbox'
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <Checkbox
                          size='small'
                          checked={isSelected}
                          disabled={!rowSelectable}
                          onChange={() => rowId !== undefined && handleRowSelectToggle(rowId)}
                          inputProps={{ 'aria-label': `select row ${rowId}` }}
                        />
                      </CustomTableCell>
                    )}
                    {columns.map((column, columnIndex) => (
                      <CustomTableCell
                        key={`custom-cell-${columnIndex}`}
                        align={column.align || 'left'}
                        sx={{
                          maxWidth: column.width,
                          minWidth: column.minWidth || '30px',
                          ...(column.groupId && {
                            borderLeft:
                              columnIndex ===
                              columns.findIndex((col) => col.groupId === column.groupId)
                                ? '1px solid #e0e0e0'
                                : undefined,
                            borderRight:
                              columnIndex ===
                              columns.map((col) => col.groupId).lastIndexOf(column.groupId)
                                ? '1px solid #e0e0e0'
                                : undefined,
                          }),
                        }}>
                        {row[column.key]}
                      </CustomTableCell>
                    ))}
                  </CustomTableRow>
                );
              })
            ) : (
              <CustomTableRow>
                <CustomTableCell
                  align='center'
                  colSpan={columns.length + (isSelectable ? 1 : 0)}>
                  {defaultText}
                </CustomTableCell>
              </CustomTableRow>
            )}
          </TableBody>
        </CustomTable>
      </CustomTableContainer>

      {pagination && (
        <CustomTableContainer sx={{ ...(maxHeight && { maxHeight }), border: 'none' }}>
          <CustomTable size={tableSize} stickyHeader={false} className='custom-table-pagination'>
            <TableFooter>
              <TableRow sx={{ border: 'none' }}>
                <CustomTablePagination
                  sx={{
                    ...(stickyPagination && {
                      position: 'sticky',
                      bottom: 0,
                      bgcolor: 'background.paper',
                      borderTop: '1px solid rgba(224, 224, 224, 1)',
                    }),
                  }}
                  rowsPerPageOptions={pagination.rowsPerPageOptions}
                  count={pagination.totalCount}
                  rowsPerPage={pagination.rowPerPage}
                  page={pagination.page - 1}
                  onPageChange={(e, page) => pagination.onPageChange(page + 1)}
                  onRowsPerPageChange={pagination.onRowsPerPageChange}
                />
              </TableRow>
            </TableFooter>
          </CustomTable>
        </CustomTableContainer>
      )}
    </>
  );
};
